// PlatformConsole — operator-only control plane for the SHARED multi-tenant
// Stage B engine. MANAGEMENT-ONLY: it lists customers and runs lifecycle
// transitions. It does NOT create customers — the Setup Wizard is the SOLE
// creation path (it drives POST /api/platform/tenants at its final step). This
// keeps one guarded, guided entry point for onboarding and one place for the
// collision/reactivate logic.
//
//   GET  /api/platform/tenants                 → list
//   POST /api/platform/tenants/:id/suspend      → status = suspended
//   POST /api/platform/tenants/:id/reactivate   → status = active
//   POST /api/platform/tenants/:id/offboard     → status = offboarded (SOFT, no delete)
//
// The operator gate is enforced SERVER-SIDE (routes/platformAdmin.js:operatorOnly,
// audited). This component is only mounted for is_operator users; that client-side
// hiding is a convenience, never the control. Editing an existing customer's
// settings (email, SSO, integrations, AI…) happens in that customer's OWN admin
// area, not here and not by re-running the wizard.

import { useState, useEffect, useCallback } from "react";
import { T } from "../theme";
import { fmtDate } from "../utils";
import { apiCall, apiCallEx } from "../apiClient";
import { Btn, Modal, Card, Badge, SectionTitle, EmptyState, RefreshBtn } from "./primitives";

const PLATFORM_TENANT_ID = "00000000-0000-0000-0000-000000000001";

interface TenantRow {
  tenant_id: string;
  name: string;
  status: string;
  edition: string;
  created_at: string;
  user_count: number | string;
}

const STATUS_STYLE: Record<string, { c: string; b: string; label: string }> = {
  active: { c: T.grn, b: T.grnS, label: "Active" },
  suspended: { c: T.ylw, b: T.ylwS, label: "Suspended" },
  offboarded: { c: T.txD, b: T.bg4, label: "Offboarded" },
};

export const PlatformConsole = () => {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Lifecycle confirm
  const [confirmOp, setConfirmOp] = useState<{ tenant: TenantRow; action: string; verb: string } | null>(null);
  const [acting, setActing] = useState(false);
  // Resend set-password invite (recovery fallback for a failed/lost auto-send)
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");

  // Operator cross-customer user management (the deliberate wall-crossing surface)
  const [manageTenant, setManageTenant] = useState<TenantRow | null>(null);
  const [manageUsers, setManageUsers] = useState<any[]>([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageErr, setManageErr] = useState("");
  const [manageMsg, setManageMsg] = useState("");
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const loadManage = useCallback(async (tenantId: string) => {
    setManageLoading(true); setManageErr(""); setManageMsg("");
    const res = await apiCallEx(`/platform/tenants/${tenantId}/users`);
    setManageLoading(false);
    if (res.ok && Array.isArray(res.data?.users)) setManageUsers(res.data.users);
    else { setManageUsers([]); setManageErr("Could not load this customer's users."); }
  }, []);

  const openManage = (t: TenantRow) => { setManageTenant(t); setManageUsers([]); setManageErr(""); setManageMsg(""); loadManage(t.tenant_id); };

  // Operator BYO AI key (support path) — write-only, validated on save.
  const [aiKeyTenant, setAiKeyTenant] = useState<TenantRow | null>(null);
  const [aiKeyConfigured, setAiKeyConfigured] = useState(false);
  const [aiKeyInput, setAiKeyInput] = useState("");
  const [aiKeyBusy, setAiKeyBusy] = useState(false);
  const [aiKeyErr, setAiKeyErr] = useState(""); const [aiKeyMsg, setAiKeyMsg] = useState("");
  const openAiKey = async (t: TenantRow) => {
    setAiKeyTenant(t); setAiKeyInput(""); setAiKeyErr(""); setAiKeyMsg(""); setAiKeyConfigured(false);
    const r = await apiCallEx(`/platform/tenants/${t.tenant_id}/ai-key`);
    if (r.ok) setAiKeyConfigured(!!r.data?.configured);
  };
  const saveAiKey = async () => {
    if (!aiKeyTenant || !aiKeyInput.trim()) return;
    setAiKeyBusy(true); setAiKeyErr(""); setAiKeyMsg("");
    const r = await apiCallEx(`/platform/tenants/${aiKeyTenant.tenant_id}/ai-key`, { method: "PUT", body: JSON.stringify({ provider: "anthropic", api_key: aiKeyInput }) });
    setAiKeyBusy(false);
    if (r.ok && r.data?.success) { setAiKeyInput(""); setAiKeyConfigured(true); setAiKeyMsg("AI key validated and saved for this customer."); }
    else setAiKeyErr(r.data?.message || (r.data?.error === "invalid_key" ? "That AI key could not be validated with the provider and was not saved." : "Could not save the key."));
  };
  const clearAiKey = async () => {
    if (!aiKeyTenant) return;
    setAiKeyBusy(true); setAiKeyErr(""); setAiKeyMsg("");
    const r = await apiCallEx(`/platform/tenants/${aiKeyTenant.tenant_id}/ai-key`, { method: "DELETE" });
    setAiKeyBusy(false);
    if (r.ok) { setAiKeyConfigured(false); setAiKeyMsg("AI key removed — this customer runs on the KontractAI default."); } else setAiKeyErr("Could not remove the key.");
  };

  const userAction = async (uid: string, action: "deactivate" | "reactivate" | "reset-invite", label: string) => {
    if (!manageTenant) return;
    setRowBusy(uid + action); setManageErr(""); setManageMsg("");
    const res = await apiCallEx(`/platform/tenants/${manageTenant.tenant_id}/users/${uid}/${action}`, { method: "POST" });
    setRowBusy(null);
    if (res.ok && res.data?.success) { setManageMsg(label); loadManage(manageTenant.tenant_id); }
    else if (res.data?.error === "last_admin") setManageErr(res.data.message || "Cannot deactivate the last active admin.");
    else if (res.status === 502) setManageErr(`Invite email could not be sent to ${res.data?.email || "the user"} — retry.`);
    else setManageErr(res.data?.message || res.data?.error || "Action failed. Please retry.");
  };

  const load = useCallback(async () => {
    setLoading(true);
    const data = await apiCall("/platform/tenants");
    if (data && Array.isArray(data.tenants)) {
      setTenants(data.tenants);
      setErr("");
    } else {
      setErr("Could not load tenants. You may not have operator access.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runLifecycle = async () => {
    if (!confirmOp) return;
    setActing(true);
    const res = await apiCall(`/platform/tenants/${confirmOp.tenant.tenant_id}/${confirmOp.action}`, { method: "POST" });
    setActing(false);
    setConfirmOp(null);
    if (!res) { setErr("Action failed. Please retry."); return; }
    load();
  };

  // Recovery fallback: re-send the admin's single-use set-password invite (no
  // password is ever shown; the email always comes from the KontractAI mailbox).
  const resendInvite = async (t: TenantRow) => {
    setResendingId(t.tenant_id); setErr(""); setNotice("");
    const res = await apiCallEx(`/platform/tenants/${t.tenant_id}/resend-invite`, { method: "POST" });
    setResendingId(null);
    if (res.ok && res.data?.ok) { setNotice(`Set-password invite re-sent to ${res.data.admin_email} from KontractAI.`); return; }
    setErr(res.data?.error === "no_admin_found" ? "No admin found for this customer." : "Could not resend the invite. Please retry.");
  };

  const th: React.CSSProperties = { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", borderBottom: `1px solid ${T.brd}` };
  const td: React.CSSProperties = { padding: "12px 14px", fontSize: 14, color: T.tx, borderBottom: `1px solid ${T.brdSubtle}`, verticalAlign: "middle" };

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <SectionTitle
        sub="Operator-only · manage customers on the shared multi-tenant engine. Create a customer in the Setup Wizard."
        right={<RefreshBtn onClick={load} />}
      >
        Platform Console
      </SectionTitle>

      {err && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 13, border: `1px solid ${T.red}` }}>
          {err}
        </div>
      )}
      {notice && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 13, border: `1px solid ${T.grn}` }}>
          {notice}
        </div>
      )}

      {loading ? (
        <Card style={{ textAlign: "center", color: T.txM, fontSize: 14 }}>Loading tenants…</Card>
      ) : tenants.length === 0 ? (
        <EmptyState icon="◇" msg="No customers yet. Use the Setup Wizard to onboard the first one." />
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Organization</th>
                <th style={th}>Status</th>
                <th style={th}>Edition</th>
                <th style={th}>Users</th>
                <th style={th}>Created</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => {
                const isPlatform = String(t.tenant_id) === PLATFORM_TENANT_ID;
                const st = STATUS_STYLE[t.status] || { c: T.txM, b: T.bg4, label: t.status };
                return (
                  <tr key={t.tenant_id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      {isPlatform && <div style={{ fontSize: 11, color: T.txD, marginTop: 2 }}>Platform tenant</div>}
                    </td>
                    <td style={td}><Badge c={st.c} b={st.b}>{st.label}</Badge></td>
                    <td style={{ ...td, textTransform: "capitalize" }}>{(t.edition || "").replace("in_house", "In-House")}</td>
                    <td style={td}>{t.user_count}</td>
                    <td style={{ ...td, color: T.txM }}>{t.created_at ? fmtDate(t.created_at) : "—"}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {isPlatform ? (
                        <span style={{ fontSize: 12, color: T.txD }}>—</span>
                      ) : (
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          {t.status === "active" && (
                            <Btn v="secondary" sz="sm" onClick={() => openManage(t)}>Manage users</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => openAiKey(t)}>AI key</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => resendInvite(t)} disabled={resendingId === t.tenant_id}>{resendingId === t.tenant_id ? "Sending…" : "Resend invite"}</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="secondary" sz="sm" onClick={() => setConfirmOp({ tenant: t, action: "suspend", verb: "Suspend" })}>Suspend</Btn>
                          )}
                          {(t.status === "suspended" || t.status === "offboarded") && (
                            <Btn v="success" sz="sm" onClick={() => setConfirmOp({ tenant: t, action: "reactivate", verb: "Reactivate" })}>Reactivate</Btn>
                          )}
                          {t.status !== "offboarded" && (
                            <Btn v="danger" sz="sm" onClick={() => setConfirmOp({ tenant: t, action: "offboard", verb: "Offboard" })}>Offboard</Btn>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* ─── Operator per-tenant BYO AI key (support) ─── */}
      <Modal open={!!aiKeyTenant} onClose={() => setAiKeyTenant(null)} title={aiKeyTenant ? `AI key · ${aiKeyTenant.name}` : ""} w={520}>
        {aiKeyTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Set this customer's own AI provider key (support). Validated on save, encrypted at rest, never shown again,
              scoped to this customer only. Removing it falls back to the KontractAI default.
            </p>
            {aiKeyErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{aiKeyErr}</div>}
            {aiKeyMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{aiKeyMsg}</div>}
            {aiKeyConfigured ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Badge c={T.grn} b={T.grnS}>Configured</Badge>
                <span style={{ fontSize: 12, color: T.txM }}>A customer key is set.</span>
                <Btn v="ghost" sz="sm" disabled={aiKeyBusy} onClick={clearAiKey}>Remove key</Btn>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input type="password" autoComplete="off" value={aiKeyInput} onChange={e => setAiKeyInput(e.target.value)} placeholder="sk-ant-…" style={{ flex: 1, minWidth: 240, padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: "monospace" }} />
                <Btn v="primary" sz="sm" disabled={aiKeyBusy || !aiKeyInput.trim()} onClick={saveAiKey}>{aiKeyBusy ? "Validating…" : "Save & validate"}</Btn>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <Btn v="primary" onClick={() => setAiKeyTenant(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Operator cross-customer user management ─── */}
      <Modal open={!!manageTenant} onClose={() => setManageTenant(null)} title={manageTenant ? `Manage users · ${manageTenant.name}` : ""} w={720}>
        {manageTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Operator-only · view, deactivate/reactivate, and re-send a set-password link for this customer's users.
              Every action is audited. <em>Role changes and impersonation are out of scope.</em>
            </p>
            {manageErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{manageErr}</div>}
            {manageMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{manageMsg}</div>}
            {manageLoading ? (
              <div style={{ textAlign: "center", color: T.txM, fontSize: 13, padding: 16 }}>Loading users…</div>
            ) : manageUsers.length === 0 ? (
              <div style={{ textAlign: "center", color: T.txM, fontSize: 13, padding: 16 }}>No users.</div>
            ) : (
              <div style={{ maxHeight: 400, overflow: "auto", border: `1px solid ${T.brd}`, borderRadius: 8 }}>
                {manageUsers.map((u: any) => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${T.brdSubtle}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{u.name || u.email}</div>
                      <div style={{ fontSize: 11, color: T.txM, fontFamily: "monospace" }}>{u.email}</div>
                    </div>
                    <span style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em" }}>{u.role}</span>
                    {u.is_active === false
                      ? <span style={{ fontSize: 10, color: T.txD, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.bg4 }}>Deactivated</span>
                      : <span style={{ fontSize: 10, color: T.grn, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.grnS }}>Active</span>}
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {u.is_active === false
                        ? <Btn v="success" sz="sm" onClick={() => userAction(u.id, "reactivate", `${u.email} reactivated.`)} disabled={rowBusy === u.id + "reactivate"}>Reactivate</Btn>
                        : <Btn v="secondary" sz="sm" onClick={() => userAction(u.id, "deactivate", `${u.email} deactivated.`)} disabled={rowBusy === u.id + "deactivate"}>Deactivate</Btn>}
                      <Btn v="ghost" sz="sm" onClick={() => userAction(u.id, "reset-invite", `Set-password link sent to ${u.email}.`)} disabled={rowBusy === u.id + "reset-invite"}>Reset password</Btn>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <Btn v="primary" onClick={() => setManageTenant(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Lifecycle confirm ─── */}
      <Modal open={!!confirmOp} onClose={() => setConfirmOp(null)} title={confirmOp ? `${confirmOp.verb} tenant` : ""} w={460}>
        {confirmOp && (
          <div>
            <p style={{ fontSize: 14, color: T.tx, margin: "0 0 8px" }}>
              {confirmOp.action === "suspend" && <>Suspend <strong>{confirmOp.tenant.name}</strong>? Its users will be blocked at sign-in (403 <code>tenant_inactive</code>) until reactivated.</>}
              {confirmOp.action === "reactivate" && <>Reactivate <strong>{confirmOp.tenant.name}</strong>? Its users will be able to sign in again. The admin password is not changed.</>}
              {confirmOp.action === "offboard" && <>Offboard <strong>{confirmOp.tenant.name}</strong>? This is a soft state (no data is deleted); users are blocked at sign-in. You can reactivate later.</>}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
              <Btn v="ghost" onClick={() => setConfirmOp(null)} disabled={acting}>Cancel</Btn>
              <Btn v={confirmOp.action === "offboard" ? "danger" : "primary"} onClick={runLifecycle} disabled={acting}>
                {acting ? "Working…" : confirmOp.verb}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
